import { createId } from "@paralleldrive/cuid2";
import {
  integer,
  sqliteTable,
  text,
  uniqueIndex,
} from "drizzle-orm/sqlite-core";
import { z } from "zod";
import { createdAt, deletedAt, lower, updatedAt } from "../lib/types";
import { cuid, dateTime, email, string, userName } from "../validator/common";

export const users = sqliteTable(
  "users",
  {
    id: text("id").primaryKey().notNull().$defaultFn(createId),
    userName: text("user_name").unique().notNull(),
    email: text("email").notNull().unique(),
    role: text("role").notNull().default("user"),
    image: text("image"),
    name: text("name").notNull().default(""),
    emailVerified: integer("email_verified", { mode: "boolean" })
      .notNull()
      .default(false),
    banned: integer("banned", { mode: "boolean" }).default(false),
    banReason: text("ban_reason"),
    banExpires: integer("ban_expires", { mode: "timestamp" }),
    notifNewContent: integer("notif_new_content", { mode: "boolean" })
      .default(true)
      .notNull(),
    notifProgramReminders: integer("notif_program_reminders", {
      mode: "boolean",
    })
      .default(true)
      .notNull(),
    notifWeekComplete: integer("notif_week_complete", { mode: "boolean" })
      .default(true)
      .notNull(),
    legalConsents: text("legal_consents", {
      mode: "json",
    }).$type<LegalConsents>(),
    createdAt,
    updatedAt,
    deletedAt,
  },
  (t) => [uniqueIndex("emailUniqueIndex").on(lower(t.email))],
);

export interface ConsentItem {
  accepted: boolean;
  acceptedAt: string;
  version: string;
}

export interface LegalConsents {
  kvkk?: ConsentItem;
  privacyPolicy?: ConsentItem;
  workoutVideoConsent?: ConsentItem;
  healthDisclaimer?: ConsentItem;
}

export const legalConsentsSchema = z.object({
  kvkk: z
    .object({
      accepted: z.boolean(),
      acceptedAt: z.string(),
      version: z.string(),
    })
    .optional(),
  privacyPolicy: z
    .object({
      accepted: z.boolean(),
      acceptedAt: z.string(),
      version: z.string(),
    })
    .optional(),
  workoutVideoConsent: z
    .object({
      accepted: z.boolean(),
      acceptedAt: z.string(),
      version: z.string(),
    })
    .optional(),
  healthDisclaimer: z
    .object({
      accepted: z.boolean(),
      acceptedAt: z.string(),
      version: z.string(),
    })
    .optional(),
});

export const selectUsersSchema = z.object({
  id: cuid,
  userName: userName.nullable(),
  email,
  role: z.enum(["user", "admin", "client", "author"]).default("user"),
  image: string.nullable(),
  legalConsents: legalConsentsSchema.nullable().optional(),
  createdAt: dateTime,
  updatedAt: dateTime.nullable(),
  deletedAt: dateTime.nullable(),
});

export const insertUsersSchema = z.object({
  userName,
  email,
});

export const patchUsersSchema = z.object({
  userName,
});
